import { yupResolver } from '@hookform/resolvers/yup';
import { CancelSharp } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { alpha, Box, Button, Card, CardActionArea, CardContent, Chip, Container, DialogActions, DialogContent, Drawer, Fab, Grid, IconButton, keyframes, Paper, Stack, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { bytesToMB, DownloadButton, enqueueSnackbar, FormProvider, RHFUpload, Spinner, useAppbarStore, useResponsive, useUserAccessStore, useUserDetailsStore, uuidv4 } from '@zeta-core';
import { DocumentAssistantIcon, MarketAnalysisIcon, MicIcon, MicListeningIcon, PDFIcon, PlusIcon, SendIconHorizontal, ZetaAiIcon, ZetaIcon } from '@zeta-core/assets';
import { IDocumentCreateSessionRequest, IDocumentCreateSessionResponse, IDocumentResponse, IDocumentShowFilesRequest, ISessionsChatResponse, WP_DOCUMENT_QA, WPDocumentQA } from '@zeta-services';
import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { RouterLinks } from '../../advisor-route-paths';
import { array, mixed, object, string } from 'yup';

import { CommonsConfig } from '../../contexts';
import { getFeaturePermission } from '../../helpers/access-control-helper';
import { PortalNameType } from '../../types';
import DashboardContainer from '../client-dashboard-layout/dashboard-container';
import { DASHBOARD_CONFIG } from '../fact-find/common/constants';
import MessageCard from './message-card';
import Sidepanel, { scrollToBottomById } from './side-panel';

export * from './message-card';

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0px rgba(0, 0, 0, 0.2);
  }
  100% {
    box-shadow: 0 0 0 20px rgba(0, 0, 0, 0);
  }
`;

// Helper: Wait until the provided stream is unlocked
export const waitForUnlocked = async (stream: ReadableStream<Uint8Array>): Promise<void> => {
  // Poll every 50ms until stream.locked is false.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (!stream.locked) {
      break;
    }
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
};

export const ERROR_MESSAGES = {
  FILE_TOO_BIG:
    'Upload Limit Exceeded : The total size of all uploaded files exceeds the 10MB limit. Please remove one or more files or upload smaller versions to proceed.',
  TOO_MANY_PAGES:
    'Upload Limit Exceeded : The document contains more than 100 pages, which exceeds the system limit. Please reduce the number of pages and try uploading again',
};

export function getErrorMessage(errorCode, defaults?: string) {
  const defaultMessage = defaults || 'Something went wrong!';
  return ERROR_MESSAGES[errorCode] || defaultMessage;
}

export const AskZeta = ({ isClient }: { isClient?: boolean }) => {
  const navigate = useNavigate();
  const { portal } = CommonsConfig();
  const userAccess = useUserAccessStore((state) => state.userAccess);
  const isReadOnly = getFeaturePermission(userAccess?.access, 'AIInteraction').hasReadPermission;
  const theme = useTheme();
  const lgUp = useResponsive('up', 'lg');
  const { id, accessibleEntityID } = useUserDetailsStore((userState) => userState.user);
  const controllerRef = useRef<AbortController | null>(null);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(null);
  const [conversations, setConversations] = useState<{ question: string; content: string; id: string; error?: boolean }[]>([]);
  const [markedIndex, setMarkedIndex] = useState<number>(-1);

  const [selectedSession, setSelectedSession] = useState({});
  const [sessions, setSessions] = useState([]);
  const session = selectedSession?.session_id;
  const [sessionId, setSessionId] = useState<string>(session);
  const [chatType, setChatType] = useState<string>('');

  const updateAppbar = useAppbarStore((state) => state?.updateAppbarStore);
  const sidebarOpen = useAppbarStore((state) => state?.sidebarOpen);

  const handleCloseSidebar = () => updateAppbar({ sidebarOpen: false });

  const getTimestamp = () => dayjs().format('YYYY-MM-DD HH:mm:ss.SSSSSS');

  const onClose = () => {
    setOpen(false);
    uploadmethods.reset({ query: '', files: [], fileNames: [] });
  };

  const methods = useForm({
    defaultValues: { query: '', files: [] },
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(object().shape({ query: string() })),
  });

  const getLatestSession = (sessions) => {
    if (!Array.isArray(sessions) || sessions.length === 0) return null;

    return sessions.reduce((latest, current) => {
      return new Date(current.updated_at) > new Date(latest.updated_at) ? current : latest;
    });
  };

  const getSessions = useMutation({
    mutationFn: () =>
      isClient ? WPDocumentQA().chatSessionsByClient({ client_id: accessibleEntityID }) : WPDocumentQA().chatSessionsByAdvisor({ advisor_id: id }),
    onSuccess: (response: AxiosResponse<ISessionsChatResponse[]>) => {
      setSessions(response.data);
      setLoading(false);
      if (response.data) {
        const findSession = response.data?.find?.((x) => x.session_id === sessionId);
        const findlatestSession = getLatestSession(response.data);

        const sessionObj = findSession ?? findlatestSession;

        showFiles.mutateAsync({
          session_id: sessionObj?.session_id,
          advisor_id: id,
        });

        setSelectedSession(sessionObj);

        setSessionId(sessionObj?.session_id);
        setConversations(sessionObj?.chat_session ?? []);
        setTimeout(() => {
          const mainElement = document.getElementById('main');
          if (mainElement) {
            mainElement.scrollTo({ top: mainElement.scrollHeight, behavior: 'smooth' });
          }
        }, 100);
      }
    },
    onError: () => {
      setLoading(false);
    },
  });

  // speak to record

  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState('');
  const silenceTimer = useRef(null); // Store the timeout reference

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let interim = '';
      // Accumulate transcript directly within the event loop
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          setTranscript((prevTranscript) => prevTranscript + event.results[i][0].transcript + '\n');
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      setInterimTranscript(interim);
    };
    recognition.onerror = (event) => {
      console.error('Speech Recognition Error:', event.error);
      setError(`Recognition error: ${event.error}`);
    };

    recognition.onnomatch = () => {
      setError('No speech was recognized.');
    };

    recognition.onend = () => {
      if (!isListening) {
        setError('Speech recognition service disconnected.');
      }
    };

    if (isListening) {
      recognition.start();
      recognition.onend = () => recognition.start(); // Restart automatically on end
    } else {
      recognition.stop();
      recognition.onend = null;
    }

    return () => {
      recognition.stop();
      recognition.onend = null;
      setError('');
    };
  }, [isListening]);

  const toggleListening = () => {
    if (isListening) {
      setIsListening(false);
      console.log('Stopped listening');
    } else {
      setIsListening(true);
      console.log('Started listening');
    }
  };

  const getMicIcon = () => {
    if (isListening) return <MicListeningIcon />;
    return <MicIcon />;
  };

  // Function to handle/reset the silence timer
  const resetSilenceTimer = () => {
    // Clear any existing timer
    if (silenceTimer.current) {
      clearTimeout(silenceTimer.current);
    }

    // Set a new timer to stop listening if no speech is detected in 10 seconds
    silenceTimer.current = setTimeout(() => {
      setIsListening(false);
      setError('');
      console.log('Auto-stopped due to silence or no speech detected');
    }, 10000);
  };

  // Effect to reset timer every time new speech is detected
  useEffect(() => {
    if (isListening) {
      resetSilenceTimer(); // Reset the timer when transcript updates
    }
  }, [transcript, isListening]);

  useEffect(() => {
    if (transcript) {
      setInput(`${transcript} ${interimTranscript}`);
    }
  }, [transcript]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInput(event.target.value);
  };

  // Smooth scroll to the bottom of the chat container
  const scrollToBottom = (): void => {
    const mainElement = document.getElementById('main');
    if (mainElement) {
      mainElement.scrollTo({ top: mainElement.scrollHeight, behavior: 'smooth' });
    }
  };

  const createSession = useMutation({
    mutationFn: (payload: IDocumentCreateSessionRequest) => WPDocumentQA().createSession(payload),
    onSuccess: (response: AxiosResponse<IDocumentCreateSessionResponse>) => {
      const res = response?.data;
      //
      setConversations([
        {
          content: res?.message,
          role: 'system',
          timestamp: getTimestamp(),
          chat_type: res.chat_type,
        },
      ]);
      setSessionId(res.session_id);
      setSelectedSession({
        session_id: res.session_id,
        client_id: res.client_id,
        chat_session: [
          {
            content: res?.message,
            role: 'system',
            timestamp: getTimestamp(),
            chat_type: res.chat_type,
          },
        ],
      });
    },
    onError: () => {
      setLoading(false);
    },
  });

  const handlecreateNew = (topicName: string) => {
    //
    setLoading(true);
    setSelectedSession(null);
    setSessionId('');
    uploadmethods.reset({
      query: '',
      files: [],
      fileNames: [],
    });
    const payload = {
      message: '',
      chat_type: 'document_analysis',
      chat_session_name: topicName,
      ...(accessibleEntityID && portal === PortalNameType.RETAIL && { client_id: accessibleEntityID }),
      ...(id && portal === PortalNameType.ADVISOR && { advisor_id: id }),
    };
    createSession
      .mutateAsync(payload)
      .then(() => {
        getSessions.mutateAsync();
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getSessions.mutateAsync();
  }, []);

  const fileSize = 10485760; // 10MB

  const uploadmethods = useForm({
    defaultValues: { query: '', files: [], fileNames: [] },
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(
      object().shape({
        query: mixed(),
        files: array(
          mixed().test('fileType', 'Unsupported File Format', (value) => {
            if (!value) return true;
            return value.type === 'application/pdf' || value.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
          })
        )
          .test('required', 'Please select at least one file', (files) => {
            return Array.isArray(files) && files.length > 0;
          })
          .test('totalSize', 'Total file size exceeds 50MB', (value) => {
            if (!value || value.length === 0) return true;
            const totalSize = value.reduce((acc, file) => acc + file.size, 0);
            return totalSize <= fileSize;
          })
          .nullable(),
      })
    ),
  });

  const uploadFiles = useMutation({
    mutationFn: (payload: FormData) => WPDocumentQA().uploadFiles(payload),
    onSuccess: (response: AxiosResponse<IDocumentResponse>) => {
      enqueueSnackbar('Files uploaded sucessfully!');
      setLoading(false);
      setOpen(false);
      uploadmethods.reset({ query: '', files: [] });
      setTimeout(() => {
        showFiles.mutateAsync({
          session_id: selectedSession?.session_id,
          advisor_id: id,
        });
      }, 1000);
    },
    onError: (res) => {
      const errorCode = res?.response?.data?.errorCode;
      setLoading(false);
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  const handleUpload = (data) => {
    //
    setLoading(true);
    const payload = new FormData();
    payload.append('session_id', selectedSession?.session_id);
    payload.append('advisor_id', id);
    for (const file of data.files) {
      payload.append('files', file); // backend expects: files: List[UploadFile]
    }

    payload.append('chat_type', 'document_analysis');
    uploadFiles.mutateAsync(payload);
  };

  const showFiles = useMutation({
    mutationFn: (payload: IDocumentShowFilesRequest) => WPDocumentQA().showFiles(payload),
    onSuccess: (response) => {
      const getFilesNames = response?.data.map((x) => x.filename) || [];
      if (getFilesNames.length > 0) {
        uploadmethods.reset({
          query: '',
          files: [],
          fileNames: getFilesNames,
        });
      }
    },
    onError: () => {
      setLoading(false);
    },
  });

  const handleQuery = async (resend?: boolean, content?: string, index?: number): Promise<void> => {
    const controller = new AbortController();
    controllerRef.current = controller;
    let payload = {};

    if (resend === true) {
      if (!content) return;
      setLoading(true);
      setEditMode({ id: -1, content: '' });
      // create resend message
      const newEntry = { content: content, role: 'user', id: uuidv4(), timestamp: getTimestamp() };

      setConversations(replaceMessageAtIndex(conversations, index, newEntry));
      setTimeout(() => {
        scrollToBottom();
      }, 100);

      payload = {
        session_id: sessionId,
        message: content,
        index: index,
        chat_type: 'document_analysis',
        stream: true,
        ...(accessibleEntityID && portal === PortalNameType.RETAIL && { client_id: accessibleEntityID }),
        ...(id && portal === PortalNameType.ADVISOR && { advisor_id: id }),
      };
    } else {
      if (!input.trim()) return;
      setLoading(true);
      // Create a new conversation entry.
      const newEntry = { content: input, role: 'user', id: uuidv4(), timestamp: getTimestamp() };

      setConversations((prev) => [...prev, newEntry]);
      setTimeout(() => {
        scrollToBottom();
      }, 100);

      payload = {
        chat_type: 'document_analysis',
        message: input,
        stream: true,
        ...(accessibleEntityID && portal === PortalNameType.RETAIL && { client_id: accessibleEntityID }),
        ...(id && portal === PortalNameType.ADVISOR && { advisor_id: id }),
        ...(sessionId && { session_id: sessionId }),
      };
    }

    setInput('');
    try {
      const token = sessionStorage.getItem('accessToken');
      const response = await fetch(`${WP_DOCUMENT_QA}/document/query-pdfs`, {
        method: resend === true ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      if (!response.body) {
        throw new Error('ReadableStream not supported or empty response body.');
      }
      // handle error code 400 and above to show error message in content as I am having an existential crisis.
      if (response.status >= 400) {
        setConversations((prevConversations) => {
          const updated = [...prevConversations];
          updated.push({
            content: 'An unexpected error occurred while processing your request. Please try again shortly.',
            role: 'system',
            showImage: true,
            id: uuidv4(),
            timestamp: getTimestamp(),
          });
          return updated;
        });
        setLoading(false);
        return;
      }

      const stream = response.body;
      // Wait until the stream is unlocked.
      await waitForUnlocked(stream);
      const reader = stream.getReader();
      const decoder = new TextDecoder('utf-8');
      let done = false;
      let buffer = '';

      // Read the stream chunk by chunk.
      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;
        // Decode the incoming chunk.
        const chunk = decoder.decode(value || new Uint8Array(), { stream: !done });
        buffer += chunk;

        // Process complete lines (each SSE event should end with a newline).
        let newlineIndex: number;
        while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
          const line = buffer.slice(0, newlineIndex).trim();
          buffer = buffer.slice(newlineIndex + 1);
          // Only process lines that start with "data:".
          if (line.startsWith('data:')) {
            const jsonStr = line.replace(/^data:\s*/, '');
            if (jsonStr) {
              try {
                const parsed = JSON.parse(jsonStr);
                // Set the sessionId if not already set.
                if (!sessionId && parsed.session_id) {
                  setSessionId(parsed.session_id);
                }
                // If a response chunk is present, append it.
                if (parsed.response !== undefined) {
                  setConversations((prevConversations) => {
                    const updated = [...prevConversations];
                    //if last message is from the system, append to it or else create a new message
                    const lastIndex = updated.length - 1 > -1 ? updated.length - 1 : 0;
                    if (updated[lastIndex]?.role !== 'user') {
                      updated[lastIndex] = { ...updated[lastIndex], role: 'system', content: updated[lastIndex]?.content + parsed.response };
                      setTimeout(() => {
                        scrollToBottom();
                      }, 100);
                    } else {
                      updated.push({ content: parsed.response, role: 'system', id: uuidv4(), timestamp: getTimestamp() });

                      setTimeout(() => {
                        scrollToBottom();
                      }, 100);
                    }
                    setTimeout(() => {
                      scrollToBottom();
                    }, 100);
                    return updated;
                  });
                }
              } catch (error) {
                console.error('Error parsing SSE JSON:', error);
                // append an error message to the conversation with content as I am having an existential crisis.
                setConversations((prevConversations) => {
                  const updated = [...prevConversations];
                  updated.push({
                    content: 'I am having an existential crisis. Please wait until I recover.',
                    role: 'system',
                    showImage: true,
                    id: uuidv4(),
                    timestamp: getTimestamp(),
                  });
                  return updated;
                });
              }
            }
          }
        }
      }
      setTranscript('');
      setInterimTranscript('');
      // Mark the conversation as complete.
      setLoading(false);
      setConversations((prevConversations) => {
        const updated = [...prevConversations];
        const lastIndex = updated.length - 1;
        updated[lastIndex] = { ...updated[lastIndex] };
        return updated;
      });
    } catch (err) {
      setLoading(false);
      console.error('Error while streaming response:', err);
      setConversations((prevConversations) => {
        const updated = [...prevConversations];
        const lastIndex = updated.length - 1;
        updated[lastIndex] = { ...updated[lastIndex], error: true };
        return updated;
      });
    }
    setInput('');
    if (resend) setEditMode(null);
  };

  const cardContents = [
    {
      title: 'Compliance Documents',
      type: 'document-assistant',
      icon: <DocumentAssistantIcon />,
      disabled: false,
      path: '',
      description:
        'Create compliance topics to organise and review required documents. Use this area as a structured checklist to confirm document presence, completeness, and relevance.',
    },
    {
      title: 'Compliance Checks',
      type: 'compliance-checks',
      icon: <MarketAnalysisIcon />,
      path: RouterLinks.complianceReviews, // can remain for now
      disabled: false,
      description:
        'Review compliance requirements, guidance, and supporting materials. Use this area to support internal checks, oversight, and ongoing governance.',
    },
  ];

  const handleCancelRequest = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
      setLoading(false);
    }
  };

  const handleSessionClick = (item) => {
    setSessionId(item?.session_id);
    setConversations?.(item?.chat_session);
    setMarkedIndex(item.processed_offset || 0);
    setSelectedSession?.(item);
    setTimeout(() => {
      scrollToBottomById('main');
      showFiles.mutateAsync({
        session_id: item?.session_id,
        advisor_id: id,
      });
    }, 50);
  };

  const files = showFiles?.data?.data;

  const downloadFile = useMutation({
    mutationFn: (payload: IDocumentShowFilesRequest) => WPDocumentQA().downloadFile(payload),
    onSuccess: (response: AxiosResponse<IDocumentResponse>) => {
      //
    },
    onError: () => {
      setLoading(false);
      enqueueSnackbar('File download failed!', { variant: 'error' });
    },
  });

  return (
    <>
      {chatType && (
        <>
          {/* renders the side panel only if the chatType is not empty */}
          {lgUp ? (
            <Sidepanel
              selectedSession={selectedSession}
              setSelectedSession={setSelectedSession}
              getSessions={getSessions}
              sessions={sessions}
              setSessionId={setSessionId}
              setInput={setInput}
              setConversations={setConversations}
              isReadOnly={isReadOnly}
              handlecreateNew={handlecreateNew}
              handleSessionClick={handleSessionClick}
            />
          ) : (
            <Drawer
              open={sidebarOpen}
              onClose={handleCloseSidebar}
              slotProps={{
                paper: {
                  sx: {
                    width: DASHBOARD_CONFIG.NAVIGATION.DESKTOP_WIDTH,
                  },
                },
              }}
            >
              <Sidepanel
                selectedSession={selectedSession}
                setSelectedSession={setSelectedSession}
                getSessions={getSessions}
                sessions={sessions}
                setSessionId={setSessionId}
                setInput={setInput}
                setConversations={setConversations}
                isReadOnly={isReadOnly}
                handlecreateNew={handlecreateNew}
                handleSessionClick={handleSessionClick}
              />
            </Drawer>
          )}
        </>
      )}
      {chatType ? (
  <DashboardContainer
    variant="sm"
    sx={{ ml: lgUp ? DASHBOARD_CONFIG.NAVIGATION.DESKTOP_WIDTH / 8 : 0 }}
  >
    <Box
      sx={{
        height: 'calc(100vh - 120px)',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.grey[100],
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      {/* =========================
          CHAT HEADER
      ========================= */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="h6">
          Compliance Document Review Workspace
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Ask questions about uploaded regulatory and financial planning documents.
          All outputs must be reviewed and approved by a suitably authorised adviser.
        </Typography>
      </Box>

      {/* =========================
          ANALYSED DOCUMENTS
      ========================= */}
      {files?.length > 0 && (
  <Box
    sx={{
      px: 3,
      py: 2.5,
      borderBottom: 1,
      borderColor: 'divider',
      bgcolor: alpha(theme.palette.primary.main, 0.04),
    }}
  >
    {/* Header */}
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
      <PDFIcon fontSize="small" sx={{ color: 'primary.main' }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Documents under review
      </Typography>
      <Typography variant="caption" color="text.secondary">
        ({files.length})
      </Typography>
    </Stack>

    {/* Document cards */}
    <Grid container spacing={1.5}>
      {files.map((file) => (
        <Grid item key={file.doc_id || file.filename} xs={12} sm={6} md={4}>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              bgcolor: 'background.paper',
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.error.main, 0.08),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <PDFIcon fontSize="small" color="error" />
            </Box>

            {/* Filename */}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {file.filename}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                PDF document
              </Typography>
            </Box>
          </Paper>
        </Grid>
      ))}
    </Grid>
  </Box>
)}


      {/* =========================
          CHAT BODY
      ========================= */}
      <Box
        id="main"
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 3,
          py: 2,
        }}
      >
        {(!conversations || conversations.length === 0) && !loading && (
          <Stack
            sx={{
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              gap: 2,
            }}
          >
            <ZetaAiIcon />
            <Typography variant="subtitle2" color="text.secondary">
              Start a new compliance review by uploading relevant documents
              and asking structured review questions.
            </Typography>
          </Stack>
        )}

        <Stack spacing={2}>
          {conversations.map((message, index) => (
            <MessageCard
              key={message?.id || index}
              index={index}
              item={message}
              editMode={editMode}
              setEditMode={setEditMode}
              handleResend={handleQuery}
              hideSave
              markedIndex={markedIndex}
              readOnly
            />
          ))}
        </Stack>
      </Box>

      {/* =========================
          INPUT BAR (STICKY)
      ========================= */}
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          px: 2,
          py: 1.5,
        }}
      >
        <FormProvider methods={methods} onSubmit={methods.handleSubmit(handleQuery)}>
          <Stack spacing={1}>
            <TextField
              autoComplete="off"
              multiline
              minRows={1}
              maxRows={4}
              value={input}
              onChange={handleChange}
              placeholder="Ask a compliance or review question about the uploaded documents…"
              fullWidth
              disabled={loading || isReadOnly}
            />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" spacing={1}>
                <Tooltip title="Upload file">
                  <Fab
                    size="small"
                    variant="soft"
                    color="default"
                    onClick={() => setOpen(true)}
                  >
                    <PlusIcon />
                  </Fab>
                </Tooltip>

                <Tooltip title="Record">
                  <Fab
                    size="small"
                    variant="soft"
                    color="default"
                    disabled={loading || isReadOnly}
                    onClick={toggleListening}
                  >
                    {getMicIcon()}
                  </Fab>
                </Tooltip>
              </Stack>

              <Stack direction="row" spacing={1}>
                <IconButton type="submit" disabled={loading}>
                  {loading ? <Spinner /> : <SendIconHorizontal />}
                </IconButton>

                {loading && (
                  <Tooltip title="Cancel">
                    <Fab
                      size="small"
                      variant="soft"
                      color="default"
                      onClick={handleCancelRequest}
                    >
                      <CancelSharp />
                    </Fab>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          </Stack>
        </FormProvider>
      </Box>
    </Box>
  </DashboardContainer>
)
 : (
        <DashboardContainer variant="sm">
          <Container maxWidth="md">
            <Stack sx={{ gap: 2.5, mb: 3, mt: 5, alignItems: 'center', textAlign: 'center' }}>
              <ZetaIcon />

              <Typography variant="h4">
                Compliance Document Review Workspace
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Review and assess regulatory, compliance, and advice-related documents
                within a structured workspace.

                This area supports compliance review and preparation only.
                All outputs must be reviewed and approved by a suitably authorised
                financial adviser before being relied upon for regulated advice.
              </Typography>
            </Stack>

            <Grid container spacing={3}>
              {cardContents.map((item, index) => (
                <Grid key={item.title} size={6} sx={{ display: 'flex' }}>
                <Card sx={{ flex: 1, display: 'flex' }}>
                    <CardContent sx={{ p: 0, pb: '0px !important' }}>
                      <CardActionArea
                            disabled={item.disabled}
                            sx={{
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              p: 3,
                              textAlign: 'center',
                            }}
                            onClick={() => {
                              if (item.path) {
                                navigate(item.path);
                                return;
                              }
                              setChatType(item.type);
                            }}
                          >
                        {item.icon}
                        <Stack
                            spacing={1}
                            sx={{
                              flexGrow: 1,
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                          <Typography variant="h5">{item.title}</Typography>
                          <Typography variant="body1" color="text.secondary">
                            {item.description}
                          </Typography>
                        </Stack>
                      </CardActionArea>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </DashboardContainer>
      )}
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': { width: '40%', background: theme.palette.background.paper, p: 3 },
          '& .MuiModal-backdrop': { background: alpha(theme.palette.common.black, 0.48) },
        }}
      >
        <DialogContent sx={{ padding: 0 }}>
          <Stack spacing={4}>
            <Typography variant="h4">Upload Documents for Review</Typography>

            <FormProvider methods={uploadmethods} onSubmit={uploadmethods.handleSubmit(handleUpload)}>
              <Grid size={12}>
                <RHFUpload
                  name="files"
                  isPDF
                  multiple
                  maxFiles={1}
                  description=""
                  subtitle="PDF only • Less than 10 MB • Under 100 pages"
                  accept={{ 'application/pdf': ['.pdf'] }}
                  validator={(fileData) => {
                    const validFileTypes = ['application/pdf'];
                    const existingFileNames = uploadmethods.watch('fileNames');
                    const currentFiles = uploadmethods.getValues('files') || []; // currently selected

                    // ❌ Reject if more than 1 file is already selected
                    if (currentFiles.length >= 1) {
                      return {
                        code: 'file-limit-exceeded',
                        message: 'Multiple files are not supported at the moment.',
                      };
                    }
                    if (!validFileTypes.includes(fileData.type)) {
                      const errorMessage = ' Only PDF files are accepted';
                      uploadmethods.setValue(`files`, { errorMessage });
                      return { code: 'file-invalid-type', message: errorMessage, type: 'file-type' };
                    }
                    if (fileData.size > fileSize) {
                      const errorMessage = `File is larger than ${Number(bytesToMB(fileSize)).toFixed(0)} MB`;
                      uploadmethods.setValue(`files`, { errorMessage });
                      return { code: 'file-too-large', message: errorMessage };
                    }
                    // Already uploaded previously
                    if (existingFileNames.includes(fileData.name)) {
                      return {
                        code: 'file-already-uploaded',
                        message: `File "${fileData.name}" has already been uploaded`,
                      };
                    }

                    // Already selected in current session
                    const isAlreadySelected = currentFiles.some((file) => file.name === fileData.name && file.size === fileData.size);
                    if (isAlreadySelected) {
                      return {
                        code: 'file-already-selected',
                        message: `File "${fileData.name}" is already selected`,
                      };
                    }
                    return null;
                  }}
                />
              </Grid>
            </FormProvider>
          </Stack>
        </DialogContent>

        <FormProvider methods={uploadmethods} onSubmit={uploadmethods.handleSubmit(handleUpload)}>
          <DialogActions sx={{ position: 'absolute', bottom: 0, right: 0, left: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Button variant="outlined" onClick={onClose} type="reset" id="cancel">
              Cancel
            </Button>

            <LoadingButton loading={loading} id="upload" disabled={loading} variant="contained" type="submit">
              Upload
            </LoadingButton>
          </DialogActions>
        </FormProvider>
      </Drawer>
    </>
  );
};

function replaceMessageAtIndex(arr, index, newMessage) {
  // Create a new array that includes elements from the start up to the specified index
  const newArr = arr.slice(0, index + 1);

  // Check if the index is within the range of the array
  if (index >= 0 && index < newArr.length) {
    // Replace the message at the specified index
    newArr[index] = newMessage;
  }

  return newArr;
}
